import React, {
  MouseEvent as ReactMouseEvent,
  ReactNode,
  RefObject,
  TouchEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
  WheelEventHandler,
} from "react";
import { useAppDispatch, useAppSelector } from "stores";
import styled from "styled-components";
import store from "stores/interfaces";
import Items from "./Panels";
import Markers from "./Markers";
import Pieces from "./Pieces";
import DiceSymbols from "./DiceSymbols";
import Field from "./Field";
import Decks from "../../Decks";
import { fixSelectionOnDragStop } from "modules/drag";
import DraggableField from "./DraggableField";
import DraggableContext from "containers/DraggableContext";
import { appStateMutate } from "stores/modules/app.state/operations";
import isInputElement from "modules/isInputElement";
import { isEqual } from "lodash-es";
import { SelectedObject } from "stores/modules/app.state";
import { OVER_EDGE_THRESHOLD, useOverDrag } from "hooks/useOverDrag";
import SelectingArea, { Size } from "./SelectingArea";
import { controlOrMetaByCurrentKey, whithControlOrMeta } from "modules/device";
import { selectRoomObjects } from "stores/modules/entities.rooms/operations";

type ScreenProps = {
  roomId: string;
  uid: string;
  centerRef: RefObject<HTMLDivElement>;
};

const Screen = (props: ScreenProps) => {
  const dispatch = useAppDispatch();
  const scale = useAppSelector((state) =>
    store.getAppState(state, "roomScreenScale")
  );
  const position = useAppSelector((state) =>
    store.getAppState(state, "roomScreenPosition")
  );
  const p = React.useRef({ x: 0, y: 0 });
  const isMultiSelectMode = useAppSelector((state) =>
    store.getAppState(state, "isMultiSelectMode")
  );
  const selectedObjects = useAppSelector((state) =>
    store.getAppState(state, "selectedObjects")
  );
  const open = useAppSelector((state) =>
    store.getAppState(state, "openRoomSelectedObjectsMenu")
  );
  const [isShift, setIsShift] = useState(false);
  const [selectingAreaSize, setSelectingAreaSize] = useState<Size | null>(null);

  const selectStartPosition = useRef<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const startObjectsRef = useRef<SelectedObject[]>([]);
  const isKeyPress = useRef(false);
  const viewRef = useRef<HTMLDivElement | null>(null);

  const onOver = useCallback((result, rect) => {
    if (!rect) return;
    if (selectStartPosition.current) {
      selectStartPosition.current.x += result.left || -result.right;
      selectStartPosition.current.y += result.top || -result.bottom;
    }

    setSelectingAreaSize((draft) => {
      if (!draft) return null;
      const vertical = adjustAxis(
        draft.top,
        draft.bottom,
        result.top,
        result.bottom,
        rect.height
      );
      const horizonal = adjustAxis(
        draft.left,
        draft.right,
        result.left,
        result.right,
        rect.width
      );
      return {
        top: vertical.start,
        bottom: vertical.end,
        left: horizonal.start,
        right: horizonal.end,
      };
    });
  }, []);

  const { onDragOver, onDragOverStop } = useOverDrag(
    viewRef.current,
    props.centerRef.current,
    onOver
  );

  const handleSelectedCancel = useCallback(
    (e: ReactMouseEvent) => {
      if (
        selectedObjects.length === 0 ||
        e.shiftKey ||
        open ||
        (isMultiSelectMode && isShift) ||
        isDragging
      )
        return;
      const target = e.target;
      if (!(target instanceof Element)) return;

      const clickedItem = target.closest("[data-field-object]");
      const clickedId = clickedItem?.getAttribute("data-field-object");
      const finedItem = !!selectedObjects.find((item) => item.id === clickedId);

      if (!finedItem) {
        dispatch(
          appStateMutate((state) => {
            state.selectedObjects = [];
          })
        );
      }
    },
    [selectedObjects, dispatch, open, isMultiSelectMode, isDragging, isShift]
  );

  useEffect(() => {
    if (selectingAreaSize && props.centerRef.current) {
      const rect = props.centerRef.current.getBoundingClientRect();
      dispatch(
        selectRoomObjects(
          selectingAreaSize,
          rect.x,
          rect.y,
          isShift,
          startObjectsRef.current
        )
      );
    }
  }, [props.centerRef, dispatch, isShift, selectingAreaSize]);

  useEffect(() => {
    if (!isMultiSelectMode) {
      setSelectingAreaSize(null);
    }
  }, [isMultiSelectMode]);

  useEffect(() => {
    const handlePressEnd = () => {
      if (isKeyPress.current) {
        isKeyPress.current = false;
        setSelectingAreaSize(null);
        dispatch(
          appStateMutate((state) => {
            state.isMultiSelectMode = !state.isMultiSelectMode;
          })
        );
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isInputElement(e)) return;

      if (e.key === "Shift") {
        setIsShift(true);
      } else if (controlOrMetaByCurrentKey(e)) {
        if (isKeyPress.current) return;
        isKeyPress.current = true;
        dispatch(
          appStateMutate((state) => {
            state.isMultiSelectMode = !state.isMultiSelectMode;
          })
        );
      } else {
        if (e.code === "KeyZ" || e.code === "KeyY") return;
        if (whithControlOrMeta(e)) {
          handlePressEnd();
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (isInputElement(e)) return;

      if (e.key === "Shift") {
        setIsShift(false);
      }
      if (controlOrMetaByCurrentKey(e)) {
        handlePressEnd();
      }
    };

    const handleFocusIn = (e: FocusEvent) => {
      if (isInputElement(e)) {
        handlePressEnd();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    document.addEventListener("blur", handlePressEnd);
    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("visibilitychange", handlePressEnd);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
      document.removeEventListener("blur", handlePressEnd);
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("visibilitychange", handlePressEnd);
    };
  }, [dispatch]);

  React.useLayoutEffect(() => {
    p.current.x = position.x;
    p.current.y = position.y;
    if (!props.centerRef.current) return;
    props.centerRef.current.style.transform = `translate(${p.current.x}px, ${p.current.y}px)`;
  }, [position, props.centerRef]);
  const onScaling = useCallback(
    (e) => {
      let nextScale;
      if (e.deltaY < 0) {
        nextScale = Math.min((scale * 10 + 1) / 10, 2.0);
      } else {
        nextScale = Math.max((scale * 10 - 1) / 10, 0.2);
      }
      const magnification = nextScale / scale;
      const nextPosition = {
        x: Math.round(position.x * magnification),
        y: Math.round(position.y * magnification),
      };
      dispatch(
        store.appStateMutate((state) => {
          state.roomScreenScale = nextScale;
          state.roomScreenPosition = nextPosition;
        })
      );
    },
    [dispatch, position, scale]
  );

  const onDragStart = useCallback(
    (e: TouchEvent | ReactMouseEvent) => {
      if (isMultiSelectMode) {
        const { clientX, clientY } = "touches" in e ? e.touches[0] : e;
        if (!isEqual(startObjectsRef.current, selectedObjects)) {
          startObjectsRef.current = selectedObjects;
        }
        selectStartPosition.current = { x: clientX, y: clientY };
      }
    },
    [isMultiSelectMode, selectedObjects]
  );
  const onDrag = useCallback(
    (e, data) => {
      if (isMultiSelectMode) {
        const { clientX, clientY } = "touches" in e ? e.touches[0] : e;
        const startPosition = selectStartPosition.current;
        if (!startPosition) return;
        const left = Math.min(startPosition.x, clientX);
        const top = Math.min(startPosition.y, clientY);
        const right = Math.max(startPosition.x, clientX);
        const bottom = Math.max(startPosition.y, clientY);
        setSelectingAreaSize({
          left,
          top,
          right,
          bottom,
        });

        onDragOver(clientX, clientY);
      } else {
        setIsDragging(true);
        if (!props.centerRef.current) return;
        p.current.x += data.deltaX;
        p.current.y += data.deltaY;
        props.centerRef.current.style.transform = `translate(${p.current.x}px, ${p.current.y}px)`;
      }
    },
    [props.centerRef, isMultiSelectMode, onDragOver]
  );

  const onDragStop = useCallback(() => {
    fixSelectionOnDragStop();
    onDragOverStop();
    if (isMultiSelectMode) {
      startObjectsRef.current = [];
      selectStartPosition.current = null;
      setSelectingAreaSize(null);
    } else {
      setIsDragging(false);
      const nextPosition = {
        x: Math.round(p.current.x),
        y: Math.round(p.current.y),
      };
      dispatch(
        store.appStateMutate((state) => {
          state.roomScreenPosition = nextPosition;
        })
      );
    }
  }, [dispatch, isMultiSelectMode, onDragOverStop]);

  return (
    <DraggableField onStart={onDragStart} onDrag={onDrag} onStop={onDragStop}>
      <SelectingArea selectingAreaSize={selectingAreaSize} />
      <DraggableContext viewRef={viewRef} centerRef={props.centerRef}>
        <View
          ref={viewRef}
          style={{
            cursor: isMultiSelectMode ? "crosshair" : undefined,
          }}
          onWheel={onScaling}
          onPointerDown={handleSelectedCancel}
        >
          <Anchor ref={props.centerRef}>
            <Scaler scale={scale}>
              <Field />
              <Decks />
              <Items />
              <Pieces roomId={props.roomId} uid={props.uid} />
              <Markers roomId={props.roomId} />
              <DiceSymbols roomId={props.roomId} />
            </Scaler>
          </Anchor>
        </View>
      </DraggableContext>
    </DraggableField>
  );
};

type ScalerProps = {
  scale: number;
  onWheel?: WheelEventHandler<HTMLDivElement>;
  children: ReactNode;
};

const Scaler = ({ scale, onWheel, children }: ScalerProps) => (
  <div style={{ transform: `scale(${scale})` }} onWheel={onWheel}>
    {children}
  </div>
);

const adjustAxis = (
  start: number,
  end: number,
  moveStart: number,
  moveEnd: number,
  max: number
) => {
  let resultStart = start;
  let resultEnd = end;
  if (moveStart > 0) {
    if (end <= OVER_EDGE_THRESHOLD && start < 0) {
      resultStart = start + moveStart;
    } else {
      resultEnd = end + moveStart;
    }
  } else if (moveEnd > 0) {
    if (start >= max - OVER_EDGE_THRESHOLD && end > max) {
      resultEnd = end - moveEnd;
    } else {
      resultStart = start - moveEnd;
    }
  }
  return {
    start: resultStart,
    end: resultEnd,
  };
};

const Anchor = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
`;

const View = styled.div`
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  outline: none;
`;

export default React.memo(Screen);
